import {
  EventType,
  type AccountInfo,
  type AuthenticationResult,
} from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useCallback, useEffect, useState } from "react";
import { loginRequest } from "../../config/auth/authConfig";
import { authService } from "../../services/authService";
import type { TokenDetails } from "../types/common.types";
import { useAppDispatch } from "@/store/hooks";
import { setAuth, clearAuth, readAuthFromStorage } from "@/store/authSlice";

export interface AppAuthState {
  jwtReady: boolean;
  accessDenied: boolean;
  accessDeniedMessage: string;
  handleLogout: () => void;
}

/**
 * Manages the application-level auth lifecycle:
 * - Exchanges the MSAL token for a backend JWT on login/SSO
 * - Refreshes the JWT every 55 minutes
 * - Exposes a handleLogout that clears storage and redirects
 */
export function useAppAuth(): AppAuthState {
  const { instance } = useMsal();
  const dispatch = useAppDispatch();
  const [jwtReady, setJwtReady] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [accessDeniedMessage, setAccessDeniedMessage] = useState("");

  const clearAuthStorage = useCallback(() => {
    ["Name", "Username", "IsAdmin", "Role", "TabPermissions"].forEach((k) =>
      localStorage.removeItem(k),
    );
    dispatch(clearAuth());
  }, [dispatch]);

  const fetchAndStoreJwt = useCallback(
    async (account: AccountInfo) => {
      try {
        const payload: TokenDetails = {
          name: account.name ?? account.username,
          email: account.username,
        };

        const token = await authService.getToken(payload);

        if (!token?.isSuccess) {
          clearAuthStorage();
          authService.logout().catch(() => {});
          setAccessDeniedMessage(token?.message ?? "");
          setAccessDenied(true);
          setJwtReady(false);
          return;
        }

        // Store only non-sensitive metadata — JWT lives in HttpOnly cookie
        localStorage.setItem("Name", payload.name!);
        localStorage.setItem("Username", account.username);
        localStorage.setItem("IsAdmin", String(token.isAdmin));

        if (token.role) {
          localStorage.setItem("Role", token.role);
          localStorage.setItem(
            "TabPermissions",
            JSON.stringify(token.tabPermissions ?? {}),
          );
        } else {
          localStorage.removeItem("Role");
          localStorage.removeItem("TabPermissions");
        }

        // Sync Redux store immediately so components reflect the latest role/permissions
        // without needing a page refresh — covers first load and the 55-min polling interval.
        dispatch(setAuth(readAuthFromStorage()));

        setAccessDenied(false);
        setJwtReady(true);
      } catch (err) {
        console.error("Failed to fetch/store JWT:", err);
        setJwtReady(false);
      }
    },
    [clearAuthStorage, dispatch],
  );

  // Handle LOGIN_SUCCESS event and fetch JWT on mount
  // Note: Login redirect is handled in main.tsx before app renders
  useEffect(() => {
    const callbackId = instance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        instance.setActiveAccount(payload.account);
        fetchAndStoreJwt(payload.account);
      }
    });

    // App only renders when accounts exist (enforced by main.tsx)
    const accounts = instance.getAllAccounts();
    if (accounts.length > 0) {
      instance.setActiveAccount(accounts[0]);
      fetchAndStoreJwt(accounts[0]);
    }

    return () => {
      if (callbackId) instance.removeEventCallback(callbackId);
    };
  }, [instance, fetchAndStoreJwt]);

  // Refresh JWT every 55 minutes (only after the first successful login)
  useEffect(() => {
    const account = instance.getActiveAccount();
    if (!account || !jwtReady) return;
    const id = setInterval(() => fetchAndStoreJwt(account), 55 * 60 * 1000);
    return () => clearInterval(id);
  }, [instance, fetchAndStoreJwt, jwtReady]);

  const handleLogout = useCallback(() => {
    ["SelectedAlertsCustomers", "SelectedProjects", "SelectedAlertsEnvironments"].forEach(
      (key) => localStorage.setItem(key, JSON.stringify([])),
    );
    ["Name", "Username", "IsAdmin", "Role", "TabPermissions"].forEach((key) =>
      localStorage.removeItem(key),
    );
    dispatch(clearAuth());
    authService
      .logout()
      .catch(() => {})
      .finally(() => {
        instance.logoutRedirect().catch((error) => console.error("Logout error:", error));
      });
  }, [instance, dispatch]);

  return { jwtReady, accessDenied, accessDeniedMessage, handleLogout };
}
